// client/src/components/ProtectedRouteClient.js
//
// ✅ FIX: Mobile bfcache restore can show stale private UI even after logout.
// ✅ FIX: Treat storage token as the source of truth + re-check on pageshow/visibilitychange.
// ✅ Client-only access enforced.
// ✅ Redirect consistently to /login (no logout query mode)

import React, { useEffect } from "react";
import { Navigate, useLocation } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import AuthLoading from "./AuthLoading";

const LOGOUT_IN_PROGRESS_KEY = "cortexLogoutInProgress";
const LOGOUT_MODE_KEY = "cortexLogoutMode";
const LAST_LOGOUT_TS_KEY = "cortexLastLogoutTs";
const RECENT_LOGOUT_LOCK_MS = 30000;

const getFlagBoth = (key) => {
  try {
    if (localStorage.getItem(key) === "1") return true;
  } catch {}
  try {
    if (sessionStorage.getItem(key) === "1") return true;
  } catch {}
  return false;
};

const isLoggingOut = () => getFlagBoth(LOGOUT_IN_PROGRESS_KEY);
const isLogoutMode = () => getFlagBoth(LOGOUT_MODE_KEY);

const getLastLogoutTs = () => {
  let v = "";
  try {
    v = localStorage.getItem(LAST_LOGOUT_TS_KEY) || "";
  } catch {}
  if (!v) {
    try {
      v = sessionStorage.getItem(LAST_LOGOUT_TS_KEY) || "";
    } catch {}
  }
  const n = Number(v);
  return Number.isFinite(n) ? n : 0;
};

const isInRecentLogoutLock = () => {
  const ts = getLastLogoutTs();
  if (!ts) return false;
  return Date.now() - ts < RECENT_LOGOUT_LOCK_MS;
};

const normalizeRole = (r) =>
  String(r || "")
    .toLowerCase()
    .replace(/[_\s]/g, "")
    .trim();

const getPortalLandingPath = (role) =>
  normalizeRole(role) === "creative" ? "/nexus-hub" : "/dashboard";

const getTokenFromStorage = () => {
  try {
    return (
      localStorage.getItem("clientAuthToken") ||
      localStorage.getItem("authToken") ||
      localStorage.getItem("token") ||
      ""
    );
  } catch {
    return "";
  }
};

const ProtectedRouteClient = ({ children, allowedRoles = ["client"] }) => {
  const location = useLocation();
  const auth = useAuth() || {};
  const { isAuthenticating, isAuthenticated, role, checkAuthentication } = auth;

  const storageToken = getTokenFromStorage();

  useEffect(() => {
    if (isLogoutMode() || isLoggingOut() || isInRecentLogoutLock()) return;
    if (!storageToken) return;
    if (typeof checkAuthentication === "function") checkAuthentication();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [storageToken, checkAuthentication]);

  useEffect(() => {
    const sync = () => {
      if (isLogoutMode() || isLoggingOut() || isInRecentLogoutLock()) return;
      const t = getTokenFromStorage();
      if (!t) return;
      if (typeof checkAuthentication === "function") checkAuthentication();
    };

    const onPageShow = () => sync();
    const onVis = () => {
      if (document.visibilityState === "visible") sync();
    };

    window.addEventListener("pageshow", onPageShow);
    document.addEventListener("visibilitychange", onVis);

    return () => {
      window.removeEventListener("pageshow", onPageShow);
      document.removeEventListener("visibilitychange", onVis);
    };
  }, [checkAuthentication]);

  if (isLogoutMode() || isLoggingOut() || isInRecentLogoutLock()) {
    return <Navigate to="/login" replace state={{ from: location }} />;
  }

  if (isAuthenticating) return <AuthLoading />;

  const rr = normalizeRole(role || "client");
  const normalizedAllowedRoles = new Set(
    (Array.isArray(allowedRoles) ? allowedRoles : ["client"]).map(normalizeRole)
  );

  if (!storageToken || !isAuthenticated) {
    return <Navigate to="/login" replace state={{ from: location }} />;
  }

  if (!normalizedAllowedRoles.has(rr)) {
    return <Navigate to={getPortalLandingPath(rr)} replace state={{ from: location }} />;
  }

  return children;
};

export default ProtectedRouteClient;
